import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/dateUtils";
import {
  MANUAL_STOP_REASONS,
  roCompletionReportValidationError,
} from "@shared/roWorkTimer";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Play } from "lucide-react";
import { useLiveElapsed } from "./useLiveElapsed";

const PAUSE_REASONS = MANUAL_STOP_REASONS.filter((reason) => !reason.completesRepair);

type Session = {
  sessionId: number;
  repairOrderId: number;
  jobNumber: string;
  equipment: string;
  customerName: string;
  technicianName: string;
  startedAt: Date | string;
  elapsedSeconds: number;
  status?: string;
};

export function RoWorkTimerCard({
  jobId,
  jobNumber,
  status,
  assignedTechnicianId,
  currentUserId,
  isAdmin,
  variant = "card",
  startMode = "auto",
}: {
  jobId: number;
  jobNumber: string;
  status: string;
  assignedTechnicianId: number | null;
  currentUserId: number | null;
  isAdmin: boolean;
  variant?: "card" | "header" | "next-action";
  /** auto keeps the historical gate. The focused page passes the shared workflow decision. */
  startMode?: "auto" | "start" | "claim" | "admin-start" | "hidden";
}) {
  const utils = trpc.useUtils();
  const active = trpc.roWorkSessions.myActive.useQuery(undefined, { refetchInterval: 15_000 });
  const session = active.data?.find((row) => row.repairOrderId === jobId) ?? null;
  const legacyCanStart = currentUserId != null
    && (isAdmin || assignedTechnicianId === currentUserId)
    && (status === "in_progress" || status === "pending_parts" || status === "diagnosis");
  const canStart = startMode === "auto" ? legacyCanStart : startMode === "start" || startMode === "claim" || startMode === "admin-start";
  const uuidRef = useRef<string | null>(null);
  const [stopOpen, setStopOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);

  const start = trpc.roWorkSessions.start.useMutation({
    onSuccess: async () => {
      uuidRef.current = null;
      setClaimOpen(false);
      await utils.roWorkSessions.myActive.invalidate();
      await utils.jobs.getById.invalidate();
    },
    onError: (error) => {
      uuidRef.current = null;
      toast.error(error.message || "Work timer did not start.");
    },
  });

  const begin = (claimUnassigned: boolean) => {
    if (!uuidRef.current) uuidRef.current = crypto.randomUUID();
    start.mutate({ jobId, sessionUuid: uuidRef.current, claimUnassigned });
  };

  if (!canStart && !session) return null;

  const onNavy = variant === "next-action";
  const startButton = (
    <Button
      type="button"
      data-testid="ro-start-work"
      aria-label="Start work"
      className={onNavy
        ? "h-11 shrink-0 rounded-xl bg-white px-4 text-sm font-semibold tracking-wide text-slate-900 hover:bg-slate-100"
        : variant === "header"
        ? "h-12 rounded-xl bg-green-600 px-5 text-sm font-semibold tracking-wide text-white hover:bg-green-700"
        : "mt-3 h-14 w-full bg-green-600 text-base font-semibold text-white hover:bg-green-700"}
      disabled={start.isPending}
      onClick={() => {
        if (startMode === "claim") {
          setClaimOpen(true);
          return;
        }
        begin(false);
      }}
    >
      <Play className="mr-1.5 h-4 w-4" aria-hidden />
      {start.isPending ? "Starting…" : "START WORK"}
    </Button>
  );

  const running = session ? (
    <RunningTimer session={session} onStop={() => setStopOpen(true)} compact={variant !== "card"} onNavy={onNavy} />
  ) : startButton;

  const claimDialog = (
    <Dialog open={claimOpen} onOpenChange={(next) => { if (!start.isPending) setClaimOpen(next); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Start work on this Repair Order?</DialogTitle>
          <DialogDescription>This Repair Order has not been assigned to a technician.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" disabled={start.isPending} onClick={() => setClaimOpen(false)}>Cancel</Button>
          <Button
            type="button"
            data-testid="ro-assign-and-start"
            disabled={start.isPending}
            onClick={() => begin(true)}
          >
            {start.isPending ? "Starting…" : "ASSIGN TO ME & START WORK"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const dialog = session ? (
    <StopWorkDialog
      open={stopOpen}
      session={session}
      jobNumber={jobNumber}
      onOpenChange={setStopOpen}
    />
  ) : null;

  if (variant === "next-action") {
    return (
      <div data-testid="ro-next-action-timer" className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
        {running}
        {dialog}
        {claimDialog}
      </div>
    );
  }

  if (variant === "header") {
    return (
      <div data-testid="ro-header-timer" className="flex flex-wrap items-center gap-3">
        {running}
        {dialog}
        {claimDialog}
      </div>
    );
  }

  return (
    <section className="rounded-lg border bg-white p-4 shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Work timer</p>
      {running}
      {dialog}
      {claimDialog}
    </section>
  );
}

function RunningTimer({ session, onStop, compact = false, onNavy = false }: { session: Session; onStop: () => void; compact?: boolean; onNavy?: boolean }) {
  const elapsed = useLiveElapsed(session.startedAt);
  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className={onNavy ? "font-mono text-3xl font-semibold tracking-tight text-white" : "font-mono text-2xl font-semibold tracking-tight"} aria-live="polite">{elapsed}</p>
        <Button type="button" data-testid="ro-stop-work" aria-label="Stop work" className="h-12 rounded-xl bg-red-600 px-5 text-white hover:bg-red-700" onClick={onStop}>
          STOP WORK
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-3">
      <p className="font-mono text-3xl font-semibold tracking-tight" aria-live="polite">{elapsed}</p>
      <p className="text-sm text-muted-foreground">Started {formatDateTime(session.startedAt)} SGT · {session.technicianName}</p>
      <Button type="button" aria-label="Stop work" className="h-12 w-full bg-red-600 text-white hover:bg-red-700" onClick={onStop}>
        STOP WORK
      </Button>
    </div>
  );
}

export function StopWorkDialog({
  open,
  onOpenChange,
  session,
  jobNumber,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: Session;
  jobNumber?: string;
}) {
  const utils = trpc.useUtils();
  const [reason, setReason] = useState<(typeof PAUSE_REASONS)[number]["value"] | "repair_completed">("end_of_day");
  const [notes, setNotes] = useState("");
  const [rawNotes, setRawNotes] = useState("");
  const [customerReport, setCustomerReport] = useState("");
  const [customerReportError, setCustomerReportError] = useState<string | null>(null);
  const [stopError, setStopError] = useState<string | null>(null);
  const stop = trpc.roWorkSessions.stop.useMutation({
    onSuccess: async () => {
      setStopError(null);
      setNotes("");
      onOpenChange(false);
      await utils.roWorkSessions.myActive.invalidate();
      await utils.roWorkSessions.wip.invalidate();
      await utils.jobs.getById.invalidate();
    },
    onError: (error) => {
      const message = error.message || "Work timer is still running.";
      setStopError(message);
      toast.error(message);
    },
  });
  const complete = trpc.roWorkSessions.complete.useMutation({
    onSuccess: async () => {
      setStopError(null);
      onOpenChange(false);
      await utils.roWorkSessions.myActive.invalidate();
      await utils.roWorkSessions.wip.invalidate();
      await utils.jobs.getById.invalidate();
    },
    onError: (error) => {
      const message = error.message || "The repair was not completed. The timer is still running.";
      setStopError(message);
      toast.error(message);
    },
  });
  const pending = stop.isPending || complete.isPending;
  const approvalPending = session.status != null && ["awaiting_commercial_review", "ready_to_send_to_customer", "awaiting_customer_confirmation"].includes(session.status);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!pending) onOpenChange(next); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Stop work{jobNumber ? ` · ${jobNumber}` : ""}</DialogTitle>
          <DialogDescription>Choose why this repair session is stopping. Pausing does not complete the repair order.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {approvalPending ? <p className="text-sm text-amber-800">Customer approval is still outstanding. Record work done in the stop notes; final repair completion and Finance handoff remain blocked until the customer decision is resolved.</p> : null}
          {MANUAL_STOP_REASONS.filter(item => !approvalPending || !item.completesRepair).map((item) => (
            <label key={item.value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={`stop-${session.sessionId}`}
                checked={reason === item.value}
                onChange={() => setReason(item.value)}
              />
              {item.label}
            </label>
          ))}
          {reason === "repair_completed" ? (
            <div className="space-y-2">
              <Textarea value={rawNotes} onChange={(event) => setRawNotes(event.target.value)} placeholder="Internal notes (not shown to the customer)" />
              <Textarea
                value={customerReport}
                onChange={(event) => {
                  const value = event.target.value;
                  setCustomerReport(value);
                  if (roCompletionReportValidationError(value) === null) setCustomerReportError(null);
                }}
                placeholder="Customer-facing service report (required)"
                aria-invalid={Boolean(customerReportError)}
                aria-describedby={customerReportError ? "ro-customer-report-error" : undefined}
                data-testid="ro-customer-report"
              />
              {customerReportError ? <p id="ro-customer-report-error" role="alert" className="text-sm text-red-700">{customerReportError}</p> : null}
            </div>
          ) : (
            <Textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder={reason === "other" ? "Describe the reason (required)" : "Notes (optional)"}
            />
          )}
          {stopError ? <p role="alert" className="text-sm text-red-700">{stopError}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={pending} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="button"
            className="bg-red-600 text-white hover:bg-red-700"
            disabled={pending}
            onClick={() => {
              if (reason === "repair_completed") {
                const validationError = roCompletionReportValidationError(customerReport);
                if (validationError) {
                  setCustomerReportError(validationError);
                  return;
                }
                complete.mutate({
                  jobId: session.repairOrderId,
                  sessionId: session.sessionId,
                  customerReport: customerReport.trim(),
                  rawNotes,
                });
                return;
              }
              stop.mutate({ sessionId: session.sessionId, reason, notes });
            }}
          >
            {pending ? "Saving…" : "Confirm stop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
